<template>
    <section class="section pesquisar-page">
        <div class="pesquisar-intro">
            <span class="pesquisar-kicker">CONSULTA RÁPIDA</span>
            <h1 class="title is-3">Pesquisar</h1>
            <p>Encontre informações cadastradas e abra a tela com os filtros preenchidos.</p>
        </div>

        <div v-if="!podeClientes && !podeOrcamentos" class="notification is-light">
            Você ainda não possui permissão de consulta para clientes ou orçamentos.
        </div>

        <div class="pesquisar-grid">
            <form v-if="podeClientes" class="pesquisar-card" @submit.prevent="abrirClientes">
                <div class="pesquisar-card-top"><span class="pesquisar-icon is-clientes"><b-icon icon="account-search-outline" /></span><span>CADASTRO</span></div>
                <h2>Clientes</h2>
                <p>Pesquise um cliente pelo nome e consulte seus dados.</p>
                <b-field label="Nome do cliente">
                    <b-input v-model.trim="cliente" icon="magnify" placeholder="Digite o nome" maxlength="100" />
                </b-field>
                <b-button native-type="submit" type="is-primary" icon-right="arrow-right" expanded>Pesquisar clientes</b-button>
            </form>

            <form v-if="podeOrcamentos" class="pesquisar-card" @submit.prevent="abrirPedidos">
                <div class="pesquisar-card-top"><span class="pesquisar-icon is-pedidos"><b-icon icon="file-document-search-outline" /></span><span>COMERCIAL</span></div>
                <h2>Pedidos</h2>
                <p>Localize pelo número do orçamento ou pedido.</p>
                <b-field label="Número">
                    <b-input v-model.trim="numero" icon="numeric" placeholder="Ex.: 1024" maxlength="20" />
                </b-field>
                <b-button native-type="submit" type="is-primary" icon-right="arrow-right" expanded>Pesquisar pedidos</b-button>
            </form>

            <form v-if="podeOrcamentos" class="pesquisar-card" @submit.prevent="abrirItens">
                <div class="pesquisar-card-top"><span class="pesquisar-icon is-itens"><b-icon icon="puzzle-outline" /></span><span>PEÇAS E SERVIÇOS</span></div>
                <h2>Itens</h2>
                <p>Veja valores unitários comerciais por peça ou serviço e cliente.</p>
                <b-field label="Peça ou serviço">
                    <b-input v-model.trim="item" icon="magnify" placeholder="Ex.: peça X" maxlength="100" required />
                </b-field>
                <b-field label="Cliente (opcional)">
                    <b-input v-model.trim="clienteItem" icon="account-outline" placeholder="Ex.: cliente Y" maxlength="100" />
                </b-field>
                <b-button native-type="submit" type="is-primary" icon-right="arrow-right" expanded>Pesquisar itens</b-button>
            </form>

            <form v-if="podeOrcamentos" class="pesquisar-card" @submit.prevent="abrirPagamentos">
                <div class="pesquisar-card-top"><span class="pesquisar-icon is-pagamentos"><b-icon icon="calendar-search" /></span><span>ORÇAMENTOS</span></div>
                <h2>Pagamentos</h2>
                <p>Consulte a data de pagamento cadastrada, condição e status do orçamento.</p>
                <b-field label="Cliente">
                    <b-input v-model.trim="clientePagamento" icon="account-outline" placeholder="Nome do cliente" maxlength="100" />
                </b-field>
                <div class="pesquisar-datas">
                    <b-field label="Data inicial"><b-input v-model="dataInicial" type="date" icon="calendar" /></b-field>
                    <b-field label="Data final"><b-input v-model="dataFinal" type="date" icon="calendar" /></b-field>
                </div>
                <b-field label="Condição de pagamento">
                    <b-select v-model="condicaoPagamentoId" expanded :loading="carregandoCondicoes">
                        <option :value="null">Todas</option>
                        <option v-for="condicao in condicoes" :key="condicao.id" :value="condicao.id">{{ condicao.nome }}</option>
                    </b-select>
                </b-field>
                <b-field label="Status do orçamento">
                    <b-select v-model="status" expanded>
                        <option :value="null">Todos</option>
                        <option v-for="opcao in statusOrcamento" :key="opcao.id" :value="opcao.id">{{ opcao.nome }}</option>
                    </b-select>
                </b-field>
                <b-button native-type="submit" type="is-primary" icon-right="arrow-right" expanded>Pesquisar pagamentos</b-button>
            </form>
        </div>
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'

export default {
    name: 'Pesquisar',
    data() {
        return {
            cliente: '', numero: '', item: '', clienteItem: '', clientePagamento: '',
            dataInicial: '', dataFinal: '', condicaoPagamentoId: null, status: null,
            condicoes: [], carregandoCondicoes: false,
            statusOrcamento: [
                { id: 1, nome: 'Em Aberto' }, { id: 2, nome: 'Enviado' },
                { id: 3, nome: 'Aprovado' }, { id: 4, nome: 'Recusado' },
                { id: 5, nome: 'Solicitação de alteração' }, { id: 6, nome: 'Cancelado' },
                { id: 7, nome: 'Análise Produção' }, { id: 8, nome: 'Análise Produção Finalizada' },
                { id: 9, nome: 'Aprovado Produção' },
            ],
        }
    },
    computed: {
        ...mapState(['unidadeId']),
        ...mapGetters(['isInRole']),
        podeClientes() { return this.isInRole('prd-clientes-consultar') },
        podeOrcamentos() { return this.isInRole('prd-orcamentos-consultar') },
    },
    watch: { unidadeId: 'carregarCondicoes' },
    created() { this.carregarCondicoes() },
    methods: {
        async carregarCondicoes() {
            this.condicoes = []
            this.condicaoPagamentoId = null
            if (!this.podeOrcamentos || !this.unidadeId) return
            this.carregandoCondicoes = true
            try {
                const resposta = await http.get('/api/prd/apoio/condicoes-pagamento', { params: { unidadeId: this.unidadeId } })
                this.condicoes = resposta.data || []
            } catch (erro) {
                this.erro(erro)
            } finally {
                this.carregandoCondicoes = false
            }
        },
        abrirClientes() {
            if (this.podeClientes) this.$router.push({ name: 'clientes', query: { procurar: this.cliente } })
        },
        abrirPedidos() {
            if (this.podeOrcamentos) this.$router.push({ name: 'prd-orcamentos', query: { procurar: this.numero, todos: '1' } })
        },
        abrirItens() {
            if (!this.podeOrcamentos || !this.item) return
            this.$router.push({ name: 'pesquisar-itens', query: { descricao: this.item, cliente: this.clienteItem } })
        },
        abrirPagamentos() {
            if (!this.podeOrcamentos) return
            if (this.dataInicial && this.dataFinal && this.dataFinal < this.dataInicial) {
                this.$buefy.toast.open({ message: 'A data final deve ser igual ou posterior à data inicial.', type: 'is-warning' })
                return
            }
            const query = { procurar: this.clientePagamento, todos: '1', pesquisa: 'pagamentos' }
            if (this.dataInicial) query.dataPagamentoInicial = this.dataInicial
            if (this.dataFinal) query.dataPagamentoFinal = this.dataFinal
            if (this.condicaoPagamentoId) query.condicaoPagamentoId = this.condicaoPagamentoId
            if (this.status) query.status = this.status
            this.$router.push({ name: 'prd-orcamentos', query })
        },
    },
}
</script>

<style scoped>
.pesquisar-page { max-width: 1260px; margin: 0 auto; }
.pesquisar-intro { margin-bottom: 1.8rem; }
.pesquisar-intro .title { margin: .3rem 0 .55rem; color: #233a30; }
.pesquisar-intro p { color: #65776e; }
.pesquisar-kicker { color: #21825b; font-size: .72rem; font-weight: 800; letter-spacing: .16em; }
.pesquisar-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.25rem; align-items: start; }
.pesquisar-card { display: flex; flex-direction: column; gap: .8rem; min-width: 0; padding: 1.55rem; background: #fff; border: 1px solid #e2eae5; border-radius: 18px; box-shadow: 0 10px 28px rgba(32, 54, 45, .06); }
.pesquisar-card h2 { margin: 0; color: #263c31; font-size: 1.3rem; font-weight: 750; }
.pesquisar-card p { margin: -.35rem 0 .35rem; color: #677a70; font-size: .91rem; }
.pesquisar-card .field { margin-bottom: .15rem; }
.pesquisar-card .button { margin-top: .4rem; }
.pesquisar-card-top { display: flex; align-items: center; justify-content: space-between; color: #86978d; font-size: .7rem; font-weight: 800; letter-spacing: .12em; }
.pesquisar-icon { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 13px; }
.is-clientes { color: #167253; background: #e8f7ef; }
.is-pedidos { color: #3467a5; background: #edf4fc; }
.is-itens { color: #8b5b1d; background: #fff4df; }
.is-pagamentos { color: #76539d; background: #f4edfb; }
.pesquisar-datas { display: grid; grid-template-columns: 1fr 1fr; gap: .7rem; }
@media (max-width: 768px) { .pesquisar-grid { grid-template-columns: 1fr; } .pesquisar-datas { grid-template-columns: 1fr; } }
</style>
